import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IUserEntitlementEntityAttributes extends IModelAttributes {
	remainingUses: number;
	expiryDate?: string; // Date string
	isActive: boolean;
	entitlementProductId: string;
	entitlementProduct?: Models.EntitlementProductEntity | Models.IEntitlementProductEntityAttributes;
	userId: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
}

@entity('UserEntitlementEntity', 'User Entitlement')
export default class UserEntitlementEntity extends Model implements IUserEntitlementEntityAttributes {
	public static excludeFromCreate: string[] = [];
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<UserEntitlementEntity, number>()
	public remainingUses: number;

	@observable
	@attribute<UserEntitlementEntity, string>()
	public expiryDate?: string;

	@observable
	@attribute<UserEntitlementEntity, boolean>()
	public isActive: boolean;

	@observable
	@attribute()
	public entitlementProductId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public entitlementProduct?: Models.EntitlementProductEntity;

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user?: Models.UserEntity;

	public get isDepleted() {
		return this.remainingUses <= 0;
	}

	public get isUsable() {
		return this.isActive && this.remainingUses > 0 && (!this.expiryDate || new Date(this.expiryDate) > new Date());
	}

	constructor(attributes?: Partial<IUserEntitlementEntityAttributes>) {
		super(attributes);
		makeObservable(this);
		this.assignAttributes(attributes);
	}

	public assignAttributes(attributes?: Partial<IUserEntitlementEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.remainingUses !== undefined) {
				this.remainingUses = attributes.remainingUses;
			}
			if (attributes.expiryDate !== undefined) {
				this.expiryDate = attributes.expiryDate;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.entitlementProductId !== undefined) {
				this.entitlementProductId = attributes.entitlementProductId;
			}
			if (attributes.entitlementProduct !== undefined) {
				if (attributes.entitlementProduct === null) {
					this.entitlementProduct = undefined;
				} else if (attributes.entitlementProduct instanceof Models.EntitlementProductEntity) {
					this.entitlementProduct = attributes.entitlementProduct;
					this.entitlementProductId = attributes.entitlementProduct.id;
				} else {
					this.entitlementProduct = new Models.EntitlementProductEntity(attributes.entitlementProduct);
					this.entitlementProductId = this.entitlementProduct.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = undefined;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
		}
	}
}
